/**
 * CompOffRules — R151 · Feb 2026
 *
 * HR-facing configurator for per-location comp-off accrual and expiry.
 * Sits alongside /app/hr/leave-types under the HR admin area.
 *
 * Backend: routes_leaves.py (comp_off_rules collection)
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Plus, Pencil, Trash2, Info, Calendar as CalendarIcon } from "lucide-react";

const BLANK = {
  location: "",
  label: "",
  min_hours_worked: 6.0,
  credit_per_day: 1.0,
  expires_in_days: 90,
  can_encash: false,
  carry_forward: false,
  on_weekend: true,
  on_holiday: true,
  min_notice_days: 1,
  enabled: true,
};

export default function CompOffRules() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/comp-off-rules`);
      setRows(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load rules")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const sorted = useMemo(() => {
    // Default (*) always first
    return [...rows].sort((a, b) => {
      if (a.location === "*" && b.location !== "*") return -1;
      if (b.location === "*" && a.location !== "*") return 1;
      return (a.location || "").localeCompare(b.location || "");
    });
  }, [rows]);

  return (
    <div className="min-h-screen" data-testid="comp-off-rules-page">
      <PageHeader
        eyebrow="HR · Policies"
        title="Comp-Off rules"
        description="How comp-off accrues and expires — configure a default and override per office location. Per user, the most specific enabled rule fires at accrual time."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">
        <div className="border border-border bg-muted/30 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="comp-off-toolbar">
          <span className="text-xs text-muted-foreground">{rows.length} rule{rows.length === 1 ? "" : "s"}</span>
          <div className="ml-auto" />
          <Button
            className="rounded-none h-9"
            onClick={() => setEditing({ __new: true, ...BLANK })}
            data-testid="comp-off-new-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add rule
          </Button>
        </div>
        {busy && rows.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-40">Location</th>
                <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold">Label</th>
                <th className="text-right px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-24">Min hrs</th>
                <th className="text-right px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-24">Credit</th>
                <th className="text-right px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-24">Expires</th>
                <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-32">Applies to</th>
                <th className="text-center px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-16">Encash</th>
                <th className="text-center px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-24">Carry-fwd</th>
                <th className="text-center px-3 py-2 uppercase tracking-widest text-[10px] font-semibold w-20">Enabled</th>
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className={`border-t border-border hover:bg-muted/20 ${!r.enabled ? "opacity-60" : ""}`} data-testid={`comp-off-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-muted-foreground" />
                      {r.location === "*" ? <em>Default</em> : r.location}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground truncate max-w-[240px]">{r.label || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.min_hours_worked}h</td>
                  <td className="px-3 py-2 text-right font-mono">{r.credit_per_day}d</td>
                  <td className="px-3 py-2 text-right font-mono">{r.expires_in_days ? `${r.expires_in_days}d` : "never"}</td>
                  <td className="px-3 py-2 text-[10px]">
                    {[r.on_weekend && "weekends", r.on_holiday && "holidays"].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td className="px-3 py-2 text-center">{r.can_encash ? "✓" : "—"}</td>
                  <td className="px-3 py-2 text-center">{r.carry_forward ? "✓" : "—"}</td>
                  <td className="px-3 py-2 text-center">{r.enabled ? "✓" : "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      className="p-1.5 hover:bg-muted border border-transparent hover:border-border"
                      title="Edit"
                      data-testid={`comp-off-edit-${r.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {r.location !== "*" && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm(`Delete rule for location "${r.location}"?`)) return;
                          try {
                            await api.delete(`/comp-off-rules/${r.id}`);
                            toast.success("Deleted");
                            load();
                          } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
                        }}
                        className="p-1.5 hover:bg-rose-50 hover:text-rose-700 border border-transparent hover:border-rose-300"
                        title="Delete"
                        data-testid={`comp-off-del-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-border bg-muted/20 p-3 flex items-start gap-2 text-xs" data-testid="comp-off-explainer">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
          <div>
            <div className="font-semibold text-foreground">How this works</div>
            <ul className="mt-1 space-y-1 text-muted-foreground list-disc ml-4">
              <li>The <em>Default</em> (<code className="bg-muted px-1">*</code>) rule applies to anyone whose office location doesn&apos;t match a specific rule.</li>
              <li>When an employee works ≥ <b>Min hrs</b> on a weekend/holiday, they earn <b>Credit</b> days.</li>
              <li>Earned comp-off <b>expires</b> after the configured days — encashment / carry-forward can extend that.</li>
              <li>Comp-off requests still respect the <b>Min notice days</b> before the target date.</li>
            </ul>
          </div>
        </div>
      </div>
      {editing && (
        <RuleEditor
          rule={editing}
          isNew={!!editing.__new}
          wsId={activeWorkspaceId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RuleEditor({ rule, isNew, wsId, onClose, onSaved }) {
  const [form, setForm] = useState({ ...rule });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!isNew && !form.location) form.location = rule.location;
    const loc = (form.location || "*").trim() || "*";
    setSaving(true);
    try {
      const payload = { ...form, location: loc };
      delete payload.__new; delete payload.id; delete payload.workspace_id;
      delete payload.created_at; delete payload.updated_at; delete payload.created_by; delete payload.updated_by;
      if (isNew) {
        await api.post(`/workspaces/${wsId}/comp-off-rules`, payload);
        toast.success("Rule created");
      } else {
        await api.patch(`/comp-off-rules/${rule.id}`, payload);
        toast.success("Rule saved");
      }
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="comp-off-editor">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "New comp-off rule" : (rule.location === "*" ? "Edit default comp-off rule" : `Edit rule · ${rule.location}`)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {rule.location !== "*" && (
            <div>
              <Label className="text-xs">Location code *</Label>
              <Input
                value={form.location || ""}
                onChange={(e) => set("location", e.target.value)}
                placeholder="e.g. mumbai, bangalore, remote-india"
                className="h-9 text-sm mt-1 font-mono"
                data-testid="comp-off-location"
                disabled={!isNew}
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Must match the value stored on employees. Use <code className="bg-muted px-1">*</code> for the workspace default.
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs">Label</Label>
            <Input
              value={form.label || ""}
              onChange={(e) => set("label", e.target.value)}
              placeholder="e.g. Mumbai comp-off policy"
              className="h-9 text-sm mt-1"
              data-testid="comp-off-label"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Min hours worked to trigger</Label>
              <Input type="number" step="0.5" min="0" max="24"
                     value={form.min_hours_worked}
                     onChange={(e) => set("min_hours_worked", Number(e.target.value))}
                     className="h-9 text-sm mt-1"
                     data-testid="comp-off-min-hours" />
            </div>
            <div>
              <Label className="text-xs">Credit per day (days)</Label>
              <Input type="number" step="0.25" min="0" max="2"
                     value={form.credit_per_day}
                     onChange={(e) => set("credit_per_day", Number(e.target.value))}
                     className="h-9 text-sm mt-1"
                     data-testid="comp-off-credit" />
            </div>
            <div>
              <Label className="text-xs">Expires in (days) — 0 = never</Label>
              <Input type="number" min="0" max="730"
                     value={form.expires_in_days}
                     onChange={(e) => set("expires_in_days", Number(e.target.value))}
                     className="h-9 text-sm mt-1"
                     data-testid="comp-off-expiry" />
            </div>
            <div>
              <Label className="text-xs">Min notice (days) when requesting</Label>
              <Input type="number" min="0" max="90"
                     value={form.min_notice_days}
                     onChange={(e) => set("min_notice_days", Number(e.target.value))}
                     className="h-9 text-sm mt-1"
                     data-testid="comp-off-notice" />
            </div>
          </div>
          <div className="border border-border p-3 space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest">Applies when working on</Label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.on_weekend} onCheckedChange={(v) => set("on_weekend", v)} data-testid="comp-off-weekend" />
              <span>Weekends</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.on_holiday} onCheckedChange={(v) => set("on_holiday", v)} data-testid="comp-off-holiday" />
              <span>Public holidays</span>
            </label>
          </div>
          <div className="border border-border p-3 space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-widest">Options</Label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.can_encash} onCheckedChange={(v) => set("can_encash", v)} data-testid="comp-off-encash" />
              <span>HR can encash unused comp-off</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.carry_forward} onCheckedChange={(v) => set("carry_forward", v)} data-testid="comp-off-carry" />
              <span>Carry forward at year-end</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="comp-off-enabled" />
              <span>Rule enabled</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving} data-testid="comp-off-save">
            {saving ? "Saving…" : (isNew ? "Create rule" : "Save rule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
